import type { Asset, AssetType } from "schemas/generated/asset";

import * as React from "react";

import { Separator } from "@replo/design-system/components/separator/Separator";
import { V1ReploAssetsGrid } from "@v1/components/assets/ReploAssetsGrid";
import { ShopifyAssetsGrid } from "@v1/components/assets/ShopifyAssetsGrid";
import { DebouncedInput } from "@v1/components/common/designSystem/Input";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { DeprecatedTabs } from "@v1/components/designSystem/tabs/Tabs";
import { selectProjectHasShopifyIntegration } from "@v1/reducers/core-reducer";
import { useEditorSelector } from "@v1/store/store";
import { ACCEPT_FILE_EXTENSIONS } from "@v2/features/assets/constants";
import { useReploFileDropZone } from "@v2/features/assets/useFileDropZone";
import { getAssetTypeFromFile } from "@v2/features/assets/useUploadAsset";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { assetUtils } from "@v2/trpc/routes/asset";
import { Search, Upload } from "lucide-react";

type ActiveTab = "reploAssets" | "shopifyAssets";

const ASSETS_TABS: { label: string; value: ActiveTab }[] = [
  {
    label: "Replo",
    value: "reploAssets",
  },
  {
    label: "Shopify",
    value: "shopifyAssets",
  },
];

type AssetsLibraryProps = {
  assetTypes?: ("image" | "video")[];
  onAssetSelected?: (asset: Asset) => void;
  shouldAllowActions?: boolean;
};

const V1AssetsLibrary: React.FC<AssetsLibraryProps> = ({
  assetTypes = ["image", "video"],
  onAssetSelected,
  shouldAllowActions = true,
}) => {
  const hasShopifyIntegration = useEditorSelector(
    selectProjectHasShopifyIntegration,
  );
  const projectId = useCurrentProjectId();
  const [activeTab, setActiveTab] = React.useState<ActiveTab>("reploAssets");
  const [searchTerm, setSearchTerm] = React.useState("");
  const {
    getRootProps,
    getInputProps,
    openFileDialog,
    droppedFilesMap,
    isDragActive,
  } = useReploFileDropZone({
    onUploadComplete: ({ url, file, id }) => {
      void assetUtils.list.invalidate();
      // NOTE (Kevin, 2025-12-17): Auto-select asset when uploaded via any method
      // (button click, local drag-drop, or external drop zone).
      // We construct a complete Asset object from available upload data to satisfy the type.
      if (onAssetSelected && file && id) {
        const assetType = getAssetTypeFromFile(file);
        const fileExtension = file.name.split(".").at(-1) ?? "";
        const asset: Asset = {
          id,
          name: file.name,
          createdAt: new Date(),
          updatedAt: new Date(),
          url,
          type: assetType as AssetType,
          sizeBytes: file.size,
          fileExtension,
          width: null,
          height: null,
          projectId: projectId ?? null,
          altText: null,
          embeddingText: null,
        };
        onAssetSelected(asset);
      }
    },
    acceptDropAssetType: ACCEPT_FILE_EXTENSIONS,
    allowClickToUpload: true,
    isDisabled: activeTab === "shopifyAssets",
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {hasShopifyIntegration && (
        <div className="px-3">
          <DeprecatedTabs
            tabsOptions={ASSETS_TABS}
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as ActiveTab)}
            defaultValue={activeTab}
            size="sm"
          />
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col gap-2 pt-1.5 px-3 pb-2 overflow-hidden">
        <div className="flex gap-2">
          <DebouncedInput
            autoComplete="off"
            placeholder="Search"
            layoutClassName="grow"
            value={searchTerm}
            onValueChange={setSearchTerm}
            startEnhancer={<Search size={12} />}
          />
          {activeTab === "reploAssets" && (
            <DeprecatedIconButton
              icon={<Upload size={12} />}
              variant="secondary"
              onClick={openFileDialog}
              size="sm"
            />
          )}
        </div>

        <Separator />

        {activeTab === "reploAssets" ? (
          <V1ReploAssetsGrid
            searchTerm={searchTerm}
            getDropzoneInputProps={getInputProps}
            getDropzoneRootProps={getRootProps}
            assetTypes={assetTypes}
            onAssetSelected={onAssetSelected}
            shouldAllowActions={shouldAllowActions}
            droppedFilesMap={droppedFilesMap}
            isDragActive={isDragActive}
          />
        ) : (
          <ShopifyAssetsGrid
            searchTerm={searchTerm}
            assetTypes={assetTypes}
            onAssetSelected={onAssetSelected}
          />
        )}
      </div>
    </div>
  );
};

export default V1AssetsLibrary;
